// Normalisers for GET /v1/booking-by-id/:id — shared by the seeker-side
// BookingDetailsPage and the astrologer-side AppointmentDetailsPage so both
// render from the exact same read.

/** Unwrap the Laravel `{ status, data }` envelope (or pass a bare record through). */
export function unwrapBookingRecord(res) {
  if (res && typeof res === 'object' && 'data' in res && res.data && typeof res.data === 'object') {
    return res.data
  }
  return res && typeof res === 'object' ? res : null
}

/**
 * Write a note value straight into the cached GET /booking-by-id read, keyed by
 * booking id. Both note editors (the appointment page's NoteEditor and the
 * in-call HostNotesCard) share this query, so mirroring here is what carries a
 * freshly-saved note across to the other page. Tolerates the { status, data }
 * envelope and the bare record alike.
 */
export function writeCachedAstrologerNote(queryClient, bookingId, value) {
  queryClient.setQueryData(['booking-details', String(bookingId)], (old) => {
    if (!old || typeof old !== 'object') return old
    if (old.data && typeof old.data === 'object') {
      return { ...old, data: { ...old.data, astrologer_note: value } }
    }
    return { ...old, astrologer_note: value }
  })
}

/** Status pill styling + label, keyed by the backend's booking statuses.
    `heroCls` is the text color used inside the dark DetailHero fact cards —
    same buckets as the dashboard's statusStyle (upcoming → blue, completed →
    green, cancelled → stone), brightened one shade for the dark band. */
export const STATUS_META = {
  pending: { label: 'Pending', cls: 'bg-amber-50 text-amber-600 border-amber-200', heroCls: 'text-blue-300', icon: '⏳' },
  confirmed: { label: 'Confirmed', cls: 'bg-green-50 text-green-600 border-green-200', heroCls: 'text-blue-300', icon: '✓' },
  rescheduled: { label: 'Rescheduled', cls: 'bg-amber-50 text-amber-600 border-amber-200', heroCls: 'text-amber-300', icon: '🔄' },
  ready_to_start: { label: 'Ready To Start', cls: 'bg-blue-50 text-blue-600 border-blue-200', heroCls: 'text-blue-300', icon: '▶' },
  in_progress: { label: 'In Progress', cls: 'bg-indigo-50 text-indigo-600 border-indigo-200', heroCls: 'text-blue-300', icon: '📹' },
  completed: { label: 'Completed', cls: 'bg-stone-100 text-stone-500 border-stone-200', heroCls: 'text-green-400', icon: '✔' },
  cancelled: { label: 'Cancelled', cls: 'bg-red-50 text-red-500 border-red-200', heroCls: 'text-stone-300', icon: '✕' },
}

export function statusMeta(status) {
  const key = String(status || '').toLowerCase()
  return (
    STATUS_META[key] || {
      label: key ? key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'Pending',
      cls: 'bg-stone-100 text-stone-500 border-stone-200',
      heroCls: 'text-blue-300',
      icon: '•',
    }
  )
}

/** Per-status action gates — mirrors the legacy portal's blocked-status lists. */
export function statusFlags(status) {
  const s = String(status || '').toLowerCase()
  const locked = ['completed', 'ready_to_start', 'in_progress', 'cancelled'].includes(s)
  return {
    isCancelled: s === 'cancelled',
    isCompleted: s === 'completed',
    /** Seeker's Join button — only once the astrologer has opened the room. */
    canJoin: ['ready_to_start', 'in_progress'].includes(s),
    /** Astrologer's Start Video button — a `confirmed` OR `rescheduled`
     * booking is paid and upcoming, so both are startable; plus re-entry into
     * an already-open room (`ready_to_start`/`in_progress`). */
    canStartVideo: ['confirmed', 'rescheduled', 'ready_to_start', 'in_progress'].includes(s),
    canCancel: !locked,
    canReschedule: !locked,
  }
}

/** Decimal-string rupees ("10000.00") → "₹10,000". Em-dash when absent. */
export function formatRate(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return '—'
  return `₹${Math.round(n).toLocaleString('en-IN')}`
}

/** Minutes → "1 hr 30 min". */
export function formatDuration(minutes) {
  const total = Number(minutes)
  if (!Number.isFinite(total) || total <= 0) return '—'
  const hrs = Math.floor(total / 60)
  const mins = total % 60
  const parts = []
  if (hrs > 0) parts.push(`${hrs} hr${hrs > 1 ? 's' : ''}`)
  if (mins > 0) parts.push(`${mins} min`)
  return parts.join(' ') || '0 min'
}

/** "2026-07-20 10:30:00" → Date (local), or null. */
function parseScheduledAt(raw) {
  if (!raw) return null
  const d = new Date(String(raw).replace(' ', 'T'))
  return Number.isNaN(d.getTime()) ? null : d
}

const timeLabel = (d) =>
  d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }).toUpperCase()

/**
 * Schedule facts for the summary table: date label, start–end slot (end
 * derived from the duration) and the formatted duration.
 */
export function scheduleFacts(booking) {
  const start = parseScheduledAt(booking?.scheduled_at)
  if (!start) return { dateLabel: '—', slotLabel: '—', durationLabel: formatDuration(booking?.duration) }
  const duration = Number(booking?.duration)
  const end = Number.isFinite(duration) && duration > 0 ? new Date(start.getTime() + duration * 60_000) : null
  return {
    dateLabel: start.toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' }),
    slotLabel: end ? `${timeLabel(start)} – ${timeLabel(end)}` : timeLabel(start),
    durationLabel: formatDuration(booking?.duration),
    start,
  }
}

/** "13:12:00" or a TIME column serialized as "1970-01-01T13:12:00.000Z" (24-hour)
 * → "01:12 PM". The clock part is read as-is, never shifted to local time —
 * it's the seeker's wall-clock birth time. Falsy/unparseable input → null. */
function formatBirthTime(raw) {
  const match = /(?:^|T)(\d{1,2}):(\d{2})/.exec(String(raw || ''))
  if (!match) return null
  const hours24 = Number(match[1])
  const minutes = match[2]
  if (!Number.isFinite(hours24) || hours24 > 23) return null
  const period = hours24 >= 12 ? 'PM' : 'AM'
  const hours12 = hours24 % 12 || 12
  return `${String(hours12).padStart(2, '0')}:${minutes} ${period}`
}

/** "2025-06-17" or "2025-06-17T00:00:00.000Z" → "17 Jun 2025". Only the
 * calendar part is used (built as local midnight), so a UTC timestamp can't
 * slide the birthday a day back/forward. Unparseable → null. */
function formatBirthDate(raw) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(raw || ''))
  if (!match) return null
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  return Number.isNaN(date.getTime())
    ? null
    : date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

/** birth_date + birth_time + place → the seeker's birth facts, blanks dropped. */
export function birthFacts(booking) {
  const date = formatBirthDate(booking?.birth_date)
  const time = formatBirthTime(booking?.birth_time)
  return { date, time, place: booking?.place || null }
}

/** Pivot-loaded lookup rows → "Vedic Astrology, Tarot". */
export function joinNames(list) {
  if (!Array.isArray(list)) return ''
  return list.map((item) => item?.name).filter(Boolean).join(', ')
}

export function astrologerDisplayName(booking) {
  const a = booking?.astrologer
  // The astrologer's real name is first_name + last_name on their linked
  // user record (rides along on booking-by-id as astrologer.user) —
  // display_name is only the fallback while the payload lacks it.
  const fullName = [
    a?.user?.first_name ?? a?.first_name,
    a?.user?.last_name ?? a?.last_name,
  ].filter(Boolean).join(' ').trim()
  return fullName || a?.display_name || a?.name || booking?.astrologer_name || 'Astrologer'
}

/** The astrologer note is only shown once the astrologer has finalized it. */
export function isNoteFinalized(booking) {
  return Boolean(
    booking?.final_confirmation_from_astrologer || booking?.astrologer_note_status === 'finalized',
  )
}

export const PAYMENT_LABELS = {
  razorpay: 'Razorpay',
  cod: 'Cash on Delivery',
}

export function paymentFacts(booking) {
  const method = String(booking?.payment_method || '').toLowerCase()
  return {
    methodLabel: PAYMENT_LABELS[method] || (method ? method.replace(/_/g, ' ') : '—'),
    transactionId: booking?.razorpay_payment_id || booking?.transaction_id || null,
    orderId: booking?.razorpay_order_id || null,
  }
}

const PHOTO_LABELS = {
  photo: 'Face Photo',
  left_palm_photo: 'Left Palm',
  right_palm_photo: 'Right Palm',
}

/**
 * Uploaded face/palm photos for the consultation. Prefers the `media` array
 * (Spatie media-library entries, keyed by `original_url`) since that's the
 * source of truth; falls back to the flat fields on the booking when `media`
 * isn't present — `photo` / `left_palm_photo` / `right_palm_photo` (S3 URLs,
 * current API) or the older `*_photo_url` names. Entries without a URL are
 * dropped, so a booking with no uploads renders no gallery at all.
 */
export function uploadedPhotos(booking) {
  if (!booking) return []
  if (Array.isArray(booking.media) && booking.media.length > 0) {
    return booking.media
      .filter((m) => m?.original_url)
      .map((m) => ({
        key: m.uuid || m.id,
        label: PHOTO_LABELS[m.collection_name] || m.collection_name || 'Photo',
        url: m.original_url,
      }))
  }
  return [
    { key: 'photo', label: PHOTO_LABELS.photo, url: booking.photo || booking.photo_url },
    { key: 'left_palm', label: PHOTO_LABELS.left_palm_photo, url: booking.left_palm_photo || booking.left_palm_photo_url },
    { key: 'right_palm', label: PHOTO_LABELS.right_palm_photo, url: booking.right_palm_photo || booking.right_palm_photo_url },
  ].filter((p) => typeof p.url === 'string' && /^https?:\/\//.test(p.url))
}
